import type { ExtensionRiskEstimate } from '@kirin/api-client/contracts/extensions';

/**
 * Best-effort mapping of known AgentMesh vocabulary onto the Alerts severity
 * badge palette (KIR-1928). AgentMesh strings are displayed as-is; this map
 * only picks the badge color. Unknown vocabulary falls through to
 * SeverityBadge's neutral 'secondary' variant, so new AgentMesh values render
 * without a code change (uncolored until mapped here).
 */
const KNOWN_RISK_LABEL_SEVERITY: Record<string, string> = {
  critical: 'critical',
  malicious: 'critical',
  high: 'high',
  high_risk: 'high',
  medium: 'medium',
  suspicious: 'medium',
  risky: 'medium',
  low: 'low',
  low_risk: 'low',
  clean: 'low',
};

/**
 * The displayed label: AgentMesh level, falling back to status. Trims BEFORE
 * coalescing — same order as the SQL rollup's coalesce(nullif(trim(...))) —
 * so a whitespace-only level falls through to the status label instead of
 * rendering "Not assessed" for a row that sorts/filters as its status.
 */
export function riskEstimateLabel(
  riskEstimate: ExtensionRiskEstimate | undefined
): string | null {
  const level = riskEstimate?.level?.trim();
  const status = riskEstimate?.status?.trim();
  return level || status || null;
}

/** Badge color for a raw AgentMesh label; unknown → neutral 'secondary'. */
export function riskLabelSeverity(label: string): string {
  return KNOWN_RISK_LABEL_SEVERITY[label.toLowerCase()] ?? 'secondary';
}

/** 'high_risk' → 'High Risk' — human form of a raw AgentMesh label. */
export function formatRiskLabel(label: string): string {
  return label
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
